// BM-H3638 (ruling A6): earlier wills. A Will must not revoke every earlier will blindly, so the client says
// which of three things is true: there is none, there are some and this Will replaces every one, or at least
// one (a Will made abroad, or for a business, digital assets or another country only) must stay in force.
// For each earlier instrument we record what the client knows. A date or a place the client does not know is
// recorded as not known, never guessed: the fee earner then makes enquiries and a supervisor approves.
// Pension expressions of wish and life policy nominations are asked about separately, because a Will does not
// ordinarily change them and clients often assume it does.

import { Checkbox, QuestionRow, RepeatList, Select, TextInput, YesNo, replaceAt, removeAt } from "./fields";

export const STATE_OPTIONS = [
  { value: "none", label: "No. I have never made a Will, a codicil or a Will abroad." },
  { value: "all_to_be_revoked", label: "Yes, and I want this Will to replace all of them." },
  { value: "some_must_survive", label: "Yes, and at least one of them should stay in force." },
];
export const NATURE_OPTIONS = [
  { value: "will", label: "A Will" },
  { value: "codicil", label: "A codicil (a change to a Will)" },
  { value: "foreign_will", label: "A Will made abroad" },
  { value: "other", label: "Something else" },
];

export function blankInstrument() {
  return {
    nature: null, jurisdiction: "", date: "", date_unknown: false,
    location: "", location_unknown: false, stays_in_force: null,
  };
}

export default function S1EarlierWills({ section, set }) {
  const ew = section.earlier_wills || {};
  const items = ew.instruments || [];
  const write = (patch) => set({ earlier_wills: { ...ew, ...patch } });
  const writeItem = (i, patch) => write({ instruments: replaceAt(items, i, { ...items[i], ...patch }) });
  const listed = ew.state === "all_to_be_revoked" || ew.state === "some_must_survive";

  return (
    <div data-testid="s1-earlier-wills" style={{ marginTop: 18 }}>
      <QuestionRow qid="s1_testator.earlier_wills.state" label="Have you ever made a Will, a codicil or a Will abroad?" testid="s1-earlier-wills-q-state">
        <Select
          testid="s1-earlier-wills-state"
          value={ew.state}
          options={STATE_OPTIONS}
          onChange={(v) => write({ state: v, ...(v === "none" ? { instruments: [] } : {}) })}
        />
      </QuestionRow>

      {ew.state === "some_must_survive" && (
        <p data-testid="s1-earlier-wills-survive-note" style={{ fontSize: 13, margin: "0 0 10px" }}>
          Your fee earner will talk to you about this. A Will that stays in force alongside this one needs care, so
          the usual wording that cancels every earlier Will cannot be used.
        </p>
      )}

      {listed && (
        <RepeatList
          testid="s1-earlier-wills-list"
          items={items}
          addLabel="Add an earlier Will"
          emptyText="Add each earlier Will, codicil or Will abroad you know of."
          onAdd={() => write({ instruments: [...items, blankInstrument()] })}
          onRemove={(i) => write({ instruments: removeAt(items, i) })}
          render={(item, i) => (
            <div style={{ display: "grid", gap: 8 }}>
              <QuestionRow qid="s1_testator.earlier_wills.instruments.nature" label="What is it?" testid={`s1-earlier-wills-${i}-q-nature`}>
                <Select testid={`s1-earlier-wills-${i}-nature`} value={item.nature} options={NATURE_OPTIONS} onChange={(v) => writeItem(i, { nature: v })} />
              </QuestionRow>
              <QuestionRow qid="s1_testator.earlier_wills.instruments.jurisdiction" label="Which country's law was it made under?" testid={`s1-earlier-wills-${i}-q-jurisdiction`}>
                <TextInput testid={`s1-earlier-wills-${i}-jurisdiction`} value={item.jurisdiction} onChange={(v) => writeItem(i, { jurisdiction: v })} />
              </QuestionRow>
              <QuestionRow qid="s1_testator.earlier_wills.instruments.date" label="When was it signed?" testid={`s1-earlier-wills-${i}-q-date`}>
                <TextInput testid={`s1-earlier-wills-${i}-date`} type="date" value={item.date} disabled={item.date_unknown === true}
                  onChange={(v) => writeItem(i, { date: v || "" })} />
                <Checkbox testid={`s1-earlier-wills-${i}-date-unknown`} checked={item.date_unknown === true} label="I do not know the date"
                  onChange={(on) => writeItem(i, { date_unknown: on, ...(on ? { date: "" } : {}) })} />
              </QuestionRow>
              <QuestionRow qid="s1_testator.earlier_wills.instruments.location" label="Where is the original, or a copy, kept?" testid={`s1-earlier-wills-${i}-q-location`}>
                <TextInput testid={`s1-earlier-wills-${i}-location`} value={item.location} disabled={item.location_unknown === true}
                  onChange={(v) => writeItem(i, { location: v })} />
                <Checkbox testid={`s1-earlier-wills-${i}-location-unknown`} checked={item.location_unknown === true} label="I do not know where it is"
                  onChange={(on) => writeItem(i, { location_unknown: on, ...(on ? { location: "" } : {}) })} />
              </QuestionRow>
              {ew.state === "some_must_survive" && (
                <QuestionRow qid="s1_testator.earlier_wills.instruments.stays_in_force" label="Should this one stay in force after you sign the new Will?" testid={`s1-earlier-wills-${i}-q-stays`}>
                  <YesNo testid={`s1-earlier-wills-${i}-stays`} value={item.stays_in_force} onChange={(v) => writeItem(i, { stays_in_force: v })} />
                </QuestionRow>
              )}
            </div>
          )}
        />
      )}

      <QuestionRow qid="s1_testator.earlier_wills.pension_nominations" label="Have you told a pension provider who should receive your pension if you die (an expression of wish)?" testid="s1-earlier-wills-q-pension">
        <YesNo testid="s1-earlier-wills-pension" value={ew.pension_nominations ?? null} onChange={(v) => write({ pension_nominations: v })} />
      </QuestionRow>
      <QuestionRow qid="s1_testator.earlier_wills.life_policy_nominations" label="Have you named who should receive a life insurance policy if you die?" testid="s1-earlier-wills-q-life-policy">
        <YesNo testid="s1-earlier-wills-life-policy" value={ew.life_policy_nominations ?? null} onChange={(v) => write({ life_policy_nominations: v })} />
      </QuestionRow>
    </div>
  );
}
